<template>
    <div class="login-form-container">                
        <div class="login-box">
            <form @submit.prevent="login()" novalidate>
                <h1>{{ $t('login.title') }}</h1>

                <tabs @onTabChanged="updateLoginType">
                    <tab :selected="loginType == 'id'" :tabid="'id'" :name="$t('login.withAccountId')" class="login-tab">
                        <div class="login-fields">
                            <p v-if="error && !hideError(error)" class="error">{{ error.message }}</p>
                            <label for="accountid">{{ $t('login.accountId') }}</label>

                            <!--  -->
                            <input
                                type="text"
                                id="accountid"
                                v-model="accountID"
                                :placeholder="$t('login.idPlaceholder')"
                                autofocus
                            />

                            <div class="totp" v-if="totpRequired">
                                <label for="login-totp-2">{{ $t('login.2faToken') }}</label>
                                <input type="text" id="login-totp-2" v-model="totpValue" />
                            </div>

                            <altcha-widget
                                v-if="loginFailed"
                                :key="loginAttemptKey"
                                ref="altchaWidgetID"
                                :challenge="altchaChallengeUrl"
                                configuration='{"hideFooter":true}'
                                data-altcha-theme="business"
                                @statechange="onAltchaStateChange"
                            ></altcha-widget>
                        </div>
                    </tab>
                    <tab :selected="loginType == 'email'" :tabid="'email'" :name="$t('login.withEmailAndPassword')" class="login-tab">
                        <div class="login-fields">
                            
                            <p v-if="error && !hideError(error)" class="error">{{ error.message }}</p>
                            <label for="login-email">{{ $t('login.email') }}</label>
                            <input type="text" id="login-email" v-model="email" autofocus />
                            <label for="login-password">{{ $t('login.password') }}</label>
                            <input type="password" id="login-password" v-model="password" autocomplete="current-password" />

                            <div class="totp" v-if="totpRequired">
                                <label for="login-totp-2">{{ $t('login.2faToken') }}</label>
                                <input type="text" id="login-totp-2" v-model="totpValue" />
                            </div>

                            <altcha-widget
                                v-if="loginFailed"
                                :key="loginAttemptKey"
                                ref="altchaWidgetEmail"
                                :challenge="altchaChallengeUrl"
                                configuration='{"hideFooter":true}'
                                data-altcha-theme="business"
                                @statechange="onAltchaStateChange"
                            ></altcha-widget>

                            <div class="forgot">
                                <router-link :to="{name:'recover-password-' + this.language }">{{ $t('login.forgotPassword') }}</router-link>
                            </div>
                        </div>
                    </tab>
                </tabs>
                <button class="btn btn-big btn-solid login-btn" :disabled="inProgress || !formValid || (loginFailed && !altchaToken)">
                    <progress-spinner v-if="inProgress" id="btn-progress" width="32" height="32" fill="#FFFFFF"/>{{ $t('login.title') }}
                </button>
            </form>
            <router-link
                :to="{name:'prices-' + this.language}"
                id="createnew"
                class="btn btn-big btn-border"
            >{{ $t('login.createNewAccount') }}</router-link>
        </div>
    </div>
</template>

<script>
import Api from "@/api/api";
import 'altcha';
import Tabs from "@/components/Tabs.vue";
import Tab from "@/components/Tab.vue";
import ProgressSpinner from "@/components/ProgressSpinner.vue";
import { mapState } from "vuex";
import { useI18n } from "vue-i18n";
import "altcha/themes/business.css";

const StatusTotpRequired = 70011;
const StatusTotpInvalid = 100010;

export default {
    name: "Login",
    components: { Tabs, Tab, ProgressSpinner },
    data() {
        return {
            accountID: "",

            totpRequired: true,
            email: "",
            password: "",

            altchaToken: "",
            loginType: "",
            loginFailed: false,
            loginAttemptKey: 0,
            // Unique per component instance — appended to the challenge URL to
            // prevent any proxy/browser from serving a cached (and possibly
            // already expired) challenge.
            instanceId: Math.random().toString(36).slice(2),

            totpValue: "",
            language: "en"
        };
    },
    created() {
        this.loginType = location.hash.replace("#", "");
        if (this.loginType != "id" && this.loginType != "email") {
            this.loginType = "id";
        }
    },
    mounted(){
        useI18n().locale.value = window.location.href.split("/")[3];
        this.language = useI18n().locale.value;
    },
    computed: {
        ...mapState({
            inProgress: (state) => state.auth.inProgress,
            error: (state) => state.auth.error,
        }),
        formValid() {
            if (this.loginType == "id" && this.accountID != "") return true;

            if (
                this.loginType == "email" &&
                this.email != "" &&
                this.password != ""
            ) {
                return true;
            }

            return false;
        },
        altchaChallengeUrl() {
            return (import.meta.env.VITE_APP_WEBAPI_URL || '') + '/web/accounts/altcha/challenge?v=' + this.instanceId + this.loginAttemptKey;
        },
        isRateLimited() {
            return this.error && this.error.status === 429;
        },
    },
    watch: {
        loginType: function () {
            this.resetForm();
        },
    },
    methods: {
        resetForm() {
            this.$store.dispatch("auth/resetErrors");
            this.password = "";
            this.altchaToken = "";
            this.totpValue = "";
            this.totpRequired = false;
            this.$refs.altchaWidgetID?.reset();
            this.$refs.altchaWidgetEmail?.reset();
        },
        hideError(error) {            
            return (
                error.status == StatusTotpRequired
            );
        },
        async login() {
            if (this.inProgress) return;
            if (this.loginFailed && !this.altchaToken) return;

            let data;

            if (this.loginType == "id") {
                data = {
                    accountID: this.accountID.trim(),
                };
            } else {
                data = {
                    email: this.email.trim(),
                    password: this.password.trim(),
                };
            }

            data.totpValue = this.totpValue;
            data.altchaToken = this.altchaToken;


            try {
                await this.$store.dispatch("auth/login", data);
            } catch (error) {
                this.processError(error);

                return;
            }

            // Login Succeeded
            // Account will redirect to clientarea in case it is a legacy login session
            if (this.$store.state.auth.isLegacy) {
                window.location = "/clientarea";
                return;
            }

            const redirect = this.$route.query.redirect
            const isAllowedRedirect = typeof redirect === "string" &&
                (redirect.startsWith("/en/account") || redirect.startsWith("/es/account"))
            if (isAllowedRedirect) {
                this.$router.push(redirect)
            } else {
                this.$router.push({ name: "account-" + this.language })
            }
        },
        processError(error) {
            this.totpRequired =
                error.status == StatusTotpRequired ||
                error.status == StatusTotpInvalid;

            // On 429 (rate-limit hard block) always hide the captcha widget,
            // regardless of whether it was already visible.
            if (error.status === 429) {
                this.altchaToken = "";
                this.loginFailed = false;
                return;
            }

            if (this.loginFailed) {
                // Captcha is already showing — increment the key to force a
                // fresh widget mount so it fetches a new challenge (not reused).
                this.altchaToken = "";
                this.loginAttemptKey++;
            } else if (error.status === Api.StatusAltchaInvalid) {
                // Server signals the captcha rate-limit has been reached:
                // show the altcha widget for the first time.
                this.altchaToken = "";
                this.loginFailed = true;
            }
        },
        onAltchaStateChange(ev) {
            if (ev.detail && ev.detail.state === 'verified') {
                this.altchaToken = ev.detail.payload || "";
            } else {
                this.altchaToken = "";
            }
        },
        updateLoginType(value) {
            this.loginType = value
        }
    },
};
</script>

<style lang="scss" scoped>
@use "@/styles/_vars.scss" as *;

.login-form-container {
    
    @media (min-width: $brk-mobile) {
        display: flex;
    }
}

.login-tab {
    padding: 16px 0px 16px 0px;
}

.login-box {
    display: flex;
    flex-direction: column;
    margin: 0px auto 40px auto;
    max-width: 480px;

    form {
        display: flex;
        flex-direction: column;
        .totp {
            display: flex;
            flex-direction: column;
        }

        .captcha {
            display: flex;
            flex-direction: column;

            .image-block {
                display: flex;
                align-items: center;
                margin-top: 16px;
                margin-bottom: 16px;
                background: #FFFFFFF0;

                a {
                    margin-left: 48px;
                }
            }

            img {
                flex-grow: 0;
            }
        }

        .login-fields {
            display: flex;
            flex-direction: column;

            altcha-widget {
                display: block;
                width: 100%;
                --altcha-max-width: 100%;
                margin-bottom: 16px;
            }

            .forgot {
                text-align: center;
                a {
                    font-size: 16px;
                    line-height: 64px;
                    text-decoration: none;
                    color: $blue;
                }
            }

            input {
                font-family: $font-main-mono;
                font-size: 18px;
                line-height: 28px;
                padding: 8px 16px;
                margin-bottom: 16px;
            }
        }

        .login-btn {
            margin-bottom: 48px;
        }
    }
}
</style>
